import { Box } from "@mui/material";

import { formatBudget } from "@/lib/formatter";
import { getDaysToEnergyShiftTargetYear } from "@/lib/time";

import { Icon } from "@/modules/common/components/Icon";
import { Tag } from "@/modules/common/components/Tag";
import { Typography } from "@/modules/common/components/Typography";
import { ENERGY_SHIFT_TARGET_YEAR } from "@/modules/common/constants";
import { CardStyled } from "@/modules/play/Components/Synthesis/Synthesis.common.styles";
import { useTeamValuesForTeam } from "@/modules/play/context/hooks/shared";
import { usePlay } from "@/modules/play/context/playContext";
import { synthesisConstants } from "@/modules/play/playerActions/constants/synthesis";
import { useTranslation } from "@/modules/translations";
import { ITeam } from "@/utils/types";

export default SynthesisBudget;

function SynthesisBudget({ team }: { team: ITeam | null }) {
  const { t } = useTranslation(["common", "countries"]);
  const { countriesData, game } = usePlay();
  const daysTo2050 = getDaysToEnergyShiftTargetYear(new Date(game.date));
  const { teamValues } = useTeamValuesForTeam({ teamId: team?.id });

  const budget = teamValues?.budgetSpent || 0;

  const meanYearlyBudgetPerInhabitant =
    budget * synthesisConstants.DAYS_IN_YEAR;
  const meanTotalBudgetPerInhabitant = budget * daysTo2050;
  const totalCountryBudget =
    (meanTotalBudgetPerInhabitant * countriesData.population) /
    synthesisConstants.MILLIARD;

  return (
    <CardStyled>
      <Box display="flex" alignItems="center" gap={1}>
        <Icon name="budget" />
        <Typography variant="h4">
          {t("synthesis.player.general-section.budget.title")}
        </Typography>
      </Box>

      <Box>
        <Typography
          dangerouslySetInnerHTML={{
            __html: t(
              "synthesis.player.general-section.budget.final-mean-budget",
              {
                startYear: new Date(game.date).getFullYear(),
                endYear: ENERGY_SHIFT_TARGET_YEAR,
                finalMeanYearlyBudgetPerInhabitant: t(
                  "unit.budget-per-year-per-person.base",
                  {
                    value: formatBudget(meanYearlyBudgetPerInhabitant, {
                      fractionDigits: 0,
                    }),
                    symbol: t("countries:country.fr.currency.symbol"),
                  },
                ),
                finalMeanTotalBudgetPerInhabitant: t("unit.budget.base", {
                  value: formatBudget(meanTotalBudgetPerInhabitant, {
                    fractionDigits: 0,
                  }),
                  symbol: t("countries:country.fr.currency.symbol"),
                }),
                finalTotalCountryBudget: t("unit.budget.giga", {
                  value: formatBudget(totalCountryBudget, {
                    fractionDigits: 0,
                  }),
                  symbol: t("countries:country.fr.currency.symbol"),
                  count: Math.ceil(totalCountryBudget),
                }),
                countryNameWithArticle: t(
                  "countries:country.fr.name-with-article",
                ),
                countryInhabitantName: t(
                  "countries:country.fr.inhabitant-name",
                ),
              },
            ),
          }}
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Tag type={"secondary"}>
          {t("unit.budget-per-year-per-inhabitant.base", {
            value: formatBudget(meanYearlyBudgetPerInhabitant, {
              fractionDigits: 0,
            }),
            symbol: t("countries:country.fr.currency.symbol"),
            countryInhabitantName: t("countries:country.fr.inhabitant-name"),
          })}
        </Tag>
      </Box>
    </CardStyled>
  );
}
